import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { TablePagination } from "@/components/common/TablePagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/useDebounce";
import {
  TenantHotelSubscriptionTransaction,
  TenantSubscriptionTransactionStatus,
} from "@/types/tenant-subscription";
import { AdminSubscriptionTransactionFilters } from "@/types/subscription-transactions";
import { useAdminSubscriptionTransactions } from "../hooks/useAdminSubscriptionTransactions";

const DEFAULT_PAGE_SIZE = 20;

const STATUS_OPTIONS: TenantSubscriptionTransactionStatus[] = [
  "PENDING",
  "PAID",
  "FAILED",
  "REFUNDED",
];

type TransactionsTabProps = {
  enabled: boolean;
};

type TransactionSortField =
  | "hotel"
  | "plan"
  | "status"
  | "amount"
  | "createdAt";
type SortDirection = "asc" | "desc" | null;

function formatAmount(amount: number, currency: string): string {
  const code = currency.trim() === "" ? "USD" : currency.trim().toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

function getStatusVariant(status: TenantSubscriptionTransactionStatus) {
  switch (status) {
    case "PAID":
      return "default";
    case "FAILED":
      return "destructive";
    case "REFUNDED":
      return "secondary";
    default:
      return "outline";
  }
}

export const TransactionsTab: React.FC<TransactionsTabProps> = ({
  enabled,
}) => {
  const { t } = useTranslation();
  const [hotelName, setHotelName] = useState("");
  const [planTitle, setPlanTitle] = useState("");
  const [status, setStatus] = useState<
    TenantSubscriptionTransactionStatus | "all"
  >("all");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState<TransactionSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const debouncedHotelName = useDebounce(hotelName, 300);
  const debouncedPlanTitle = useDebounce(planTitle, 300);

  const filters = useMemo((): AdminSubscriptionTransactionFilters => {
    const offset = (currentPage - 1) * pageSize;

    return {
      hotelName: debouncedHotelName.trim() || undefined,
      planTitle: debouncedPlanTitle.trim() || undefined,
      status: status === "all" ? undefined : status,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      limit: pageSize,
      offset,
    };
  }, [
    createdFrom,
    createdTo,
    currentPage,
    debouncedHotelName,
    debouncedPlanTitle,
    pageSize,
    status,
  ]);

  const { data, isLoading } = useAdminSubscriptionTransactions(
    enabled,
    filters,
  );

  const transactions = useMemo(() => {
    const rows = [...(data?.items ?? [])];

    if (sortDirection === null) {
      return rows;
    }

    const getSortValue = (
      row: TenantHotelSubscriptionTransaction,
    ): number | string => {
      switch (sortField) {
        case "hotel":
          return row.hotel.name.toLowerCase();
        case "plan":
          return row.plan.title.toLowerCase();
        case "status":
          return row.status;
        case "amount":
          return row.amount;
        case "createdAt":
        default:
          return new Date(row.createdAt).getTime();
      }
    };

    rows.sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);

      if (leftValue < rightValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return rows;
  }, [data?.items, sortDirection, sortField]);
  const total = data?.total ?? 0;
  const responsePageSize = data?.limit ?? pageSize;
  const totalPages = Math.max(1, Math.ceil(total / responsePageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const hasActiveFilters =
    hotelName.trim() !== "" ||
    planTitle.trim() !== "" ||
    status !== "all" ||
    createdFrom !== "" ||
    createdTo !== "";

  const handleResetFilters = () => {
    setHotelName("");
    setPlanTitle("");
    setStatus("all");
    setCreatedFrom("");
    setCreatedTo("");
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSortField("createdAt");
    setSortDirection("desc");
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const columns: Column<TenantHotelSubscriptionTransaction>[] = useMemo(
    () => [
      {
        key: "hotel",
        header: t("subscriptions.transactions.table.hotel"),
        sortable: true,
        render: (_: unknown, row) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-950">
              {row.hotel.name}
            </p>
          </div>
        ),
      },
      {
        key: "plan",
        header: t("subscriptions.transactions.table.plan"),
        sortable: true,
        render: (_: unknown, row) => (
          <div className="min-w-0">
            <p className="truncate">{row.plan.title}</p>
          </div>
        ),
      },
      {
        key: "status",
        header: t("subscriptions.transactions.table.status"),
        className: "text-center",
        sortable: true,
        render: (value: TenantSubscriptionTransactionStatus) => (
          <Badge variant={getStatusVariant(value)}>{value}</Badge>
        ),
      },
      {
        key: "amount",
        header: t("subscriptions.transactions.table.amount"),
        className: "text-right",
        sortable: true,
        render: (value: number, row) => formatAmount(value, row.currency),
      },
      {
        key: "stripeInvoiceId",
        header: t("subscriptions.transactions.table.invoiceId"),
        render: (value: string, row) =>
          row.invoicePdfUrl ? (
            <a
              href={row.invoicePdfUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex max-w-[220px] items-center gap-1 text-xs underline-offset-4 hover:underline"
            >
              <span className="line-clamp-1">{value}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          ) : (
            <span className="line-clamp-1 max-w-[180px] text-xs">{value}</span>
          ),
      },
      {
        key: "invoicePdfUrl",
        header: t("subscriptions.transactions.table.invoicePdf"),
        className: "text-center",
        render: (_: string | null, row) =>
          row.invoicePdfUrl ? (
            <a
              href={row.invoicePdfUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary text-sm underline-offset-4 hover:underline"
            >
              {t("subscriptions.transactions.table.openPdf")}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "createdAt",
        header: t("subscriptions.transactions.table.createdAt"),
        sortable: true,
        render: (value: string) => format(new Date(value), "dd MMM yyyy HH:mm"),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {t("subscriptions.transactions.summary.total", { count: total })}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          {t("subscriptions.transactions.filters.reset")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Input
          label={t("subscriptions.transactions.filters.hotelName")}
          value={hotelName}
          onChange={(e) => {
            setHotelName(e.target.value);
            setCurrentPage(1);
          }}
        />
        <Input
          label={t("subscriptions.transactions.filters.planTitle")}
          value={planTitle}
          onChange={(e) => {
            setPlanTitle(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm leading-none font-medium text-zinc-900">
            {t("subscriptions.transactions.filters.status")}
          </span>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value as TenantSubscriptionTransactionStatus | "all");
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("subscriptions.transactions.filters.allStatuses")}
              </SelectItem>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          type="datetime-local"
          label={t("subscriptions.transactions.filters.createdFrom")}
          value={createdFrom}
          onChange={(e) => {
            setCreatedFrom(e.target.value);
            setCurrentPage(1);
          }}
        />
        <Input
          type="datetime-local"
          label={t("subscriptions.transactions.filters.createdTo")}
          value={createdTo}
          onChange={(e) => {
            setCreatedTo(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <DataTable<TenantHotelSubscriptionTransaction>
        data={transactions}
        columns={columns}
        loading={isLoading}
        withPagination={false}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: (field) => {
            const nextField = field as TransactionSortField;

            if (sortField !== nextField) {
              setSortField(nextField);
              setSortDirection("asc");
              return;
            }

            setSortDirection((currentDirection) => {
              if (currentDirection === "asc") {
                return "desc";
              }
              if (currentDirection === "desc") {
                return null;
              }
              return "asc";
            });
          },
        }}
        emptyMessage={t("subscriptions.transactions.table.empty")}
      />

      <TablePagination
        page={currentPage}
        pageSize={responsePageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};
